import { useState, useEffect } from 'react';
import { 
  Accessibility, 
  ZoomIn, 
  ZoomOut, 
  Contrast, 
  Type, 
  Link2, 
  Pause, 
  MousePointer, 
  X,
  RotateCcw
} from 'lucide-react';

// ═══════════════════════════════════════════
// ACCESSIBILITY WIDGET
// ═══════════════════════════════════════════

export const AccessibilityWidget = ({ onOpenAccessibilityPage }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState({
    fontSize: 0, // -2 to +4 steps
    highContrast: false,
    readableFont: false,
    highlightLinks: false,
    reduceMotion: false,
    largeCursor: false,
  });

  // Load saved settings on mount
  useEffect(() => {
    const saved = localStorage.getItem('accessibilitySettings');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSettings(parsed);
        applySettings(parsed);
      } catch (e) {
        // Invalid saved data
      }
    }
  }, []);

  // Apply settings to document
  const applySettings = (newSettings) => {
    const root = document.documentElement;
    const body = document.body;

    // Font size
    const baseFontSize = 16 + (newSettings.fontSize * 2);
    root.style.fontSize = `${baseFontSize}px`;

    // High contrast
    if (newSettings.highContrast) {
      body.classList.add('high-contrast');
    } else {
      body.classList.remove('high-contrast');
    }

    // Readable font
    if (newSettings.readableFont) {
      body.classList.add('readable-font');
    } else {
      body.classList.remove('readable-font');
    }

    // Highlight links
    if (newSettings.highlightLinks) {
      body.classList.add('highlight-links');
    } else {
      body.classList.remove('highlight-links');
    }

    // Reduce motion
    if (newSettings.reduceMotion) {
      body.classList.add('reduce-motion');
    } else {
      body.classList.remove('reduce-motion');
    }

    // Large cursor
    if (newSettings.largeCursor) {
      body.classList.add('large-cursor');
    } else {
      body.classList.remove('large-cursor');
    }
  };

  const updateSetting = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    localStorage.setItem('accessibilitySettings', JSON.stringify(newSettings));
    applySettings(newSettings);
  };

  const increaseFontSize = () => {
    if (settings.fontSize < 4) {
      updateSetting('fontSize', settings.fontSize + 1);
    }
  };

  const decreaseFontSize = () => {
    if (settings.fontSize > -2) {
      updateSetting('fontSize', settings.fontSize - 1);
    }
  };

  const resetSettings = () => {
    const defaultSettings = {
      fontSize: 0,
      highContrast: false,
      readableFont: false,
      highlightLinks: false,
      reduceMotion: false,
      largeCursor: false,
    };
    setSettings(defaultSettings);
    localStorage.setItem('accessibilitySettings', JSON.stringify(defaultSettings));
    applySettings(defaultSettings);
  };

  const toggleSetting = (key) => {
    updateSetting(key, !settings[key]);
  };

  return (
    <>
      {/* Accessibility Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed left-4 top-2/3 -translate-y-1/2 z-[9997] w-12 h-12 rounded-full bg-[#2C2C2C] text-white flex items-center justify-center shadow-lg hover:bg-[#C4956A] transition-colors focus:outline-none focus:ring-4 focus:ring-[#C4956A]/50"
        aria-label="פתיחת תפריט נגישות"
        aria-expanded={isOpen}
        data-testid="accessibility-toggle"
      >
        <Accessibility className="w-6 h-6" />
      </button>

      {/* Accessibility Panel */}
      {isOpen && (
        <div
          className="fixed left-4 top-1/2 -translate-y-1/2 z-[9998] w-72 bg-white rounded-2xl shadow-2xl border border-[#E5E0DA] overflow-hidden"
          role="dialog"
          aria-label="הגדרות נגישות"
          dir="rtl"
        >
          {/* Header */}
          <div className="bg-[#2C2C2C] text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Accessibility className="w-5 h-5" />
              <h2 className="font-['Heebo'] font-bold">הגדרות נגישות</h2>
            </div>
            <button 
              onClick={() => setIsOpen(false)}
              className="hover:bg-white/20 rounded-full p-1 transition-colors"
              aria-label="סגירת תפריט נגישות"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Settings */}
          <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto">
            {/* Font Size */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-[#2C2C2C]">גודל טקסט</p>
              <div className="flex items-center gap-2">
                <button
                  onClick={decreaseFontSize}
                  className="flex-1 p-2 rounded-lg bg-[#FAF7F2] hover:bg-[#E5E0DA] transition-colors flex items-center justify-center"
                  aria-label="הקטנת טקסט"
                  disabled={settings.fontSize <= -2}
                >
                  <ZoomOut className="w-5 h-5" />
                </button>
                <span className="w-12 text-center font-medium">{100 + (settings.fontSize * 12)}%</span>
                <button
                  onClick={increaseFontSize}
                  className="flex-1 p-2 rounded-lg bg-[#FAF7F2] hover:bg-[#E5E0DA] transition-colors flex items-center justify-center"
                  aria-label="הגדלת טקסט"
                  disabled={settings.fontSize >= 4}
                >
                  <ZoomIn className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Toggle Options */}
            <ToggleOption
              icon={<Contrast className="w-5 h-5" />}
              label="ניגודיות גבוהה"
              checked={settings.highContrast}
              onChange={() => toggleSetting('highContrast')}
            />

            <ToggleOption
              icon={<Type className="w-5 h-5" />}
              label="גופן קריא"
              checked={settings.readableFont}
              onChange={() => toggleSetting('readableFont')}
            />

            <ToggleOption
              icon={<Link2 className="w-5 h-5" />}
              label="הדגשת קישורים"
              checked={settings.highlightLinks}
              onChange={() => toggleSetting('highlightLinks')}
            />

            <ToggleOption
              icon={<Pause className="w-5 h-5" />}
              label="עצירת אנימציות"
              checked={settings.reduceMotion}
              onChange={() => toggleSetting('reduceMotion')}
            />

            <ToggleOption
              icon={<MousePointer className="w-5 h-5" />}
              label="סמן גדול"
              checked={settings.largeCursor}
              onChange={() => toggleSetting('largeCursor')}
            />

            {/* Reset Button */}
            <button
              onClick={resetSettings}
              className="w-full p-3 rounded-lg border border-[#E5E0DA] text-[#2C2C2C] hover:bg-[#FAF7F2] transition-colors flex items-center justify-center gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              איפוס הגדרות
            </button>

            {/* Link to Accessibility Statement */}
            <button
              onClick={() => {
                setIsOpen(false);
                onOpenAccessibilityPage();
              }}
              className="w-full p-3 rounded-lg bg-[#C4956A] text-white hover:bg-[#b38559] transition-colors text-center"
            >
              הצהרת נגישות
            </button>
          </div>
        </div>
      )}
    </>
  );
};

// Toggle Option Component
const ToggleOption = ({ icon, label, checked, onChange }) => (
  <button
    onClick={onChange}
    className={`w-full p-3 rounded-lg border transition-colors flex items-center gap-3 ${
      checked 
        ? 'bg-[#C4956A] text-white border-[#C4956A]' 
        : 'bg-[#FAF7F2] text-[#2C2C2C] border-transparent hover:border-[#C4956A]'
    }`}
    role="switch"
    aria-checked={checked}
  >
    {icon}
    <span className="flex-1 text-right">{label}</span>
    <span className={`w-4 h-4 rounded-full ${checked ? 'bg-white' : 'bg-[#C4956A]/30'}`} />
  </button>
);

export default AccessibilityWidget;
